import React, { useEffect, useState } from "react";
import { Download, X, Share, MoreVertical } from "lucide-react";

const DISMISS_KEY = "okuapem_install_dismissed";
const isMobile = () => window.matchMedia("(max-width: 1023px)").matches && ("ontouchstart" in window || navigator.maxTouchPoints > 0);
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [ios, setIos] = useState(false);
  const [ready, setReady] = useState(false);
  // Dismissal flag only (non-sensitive UI preference) — safe in localStorage.
  const [hidden, setHidden] = useState(() => !!localStorage.getItem(DISMISS_KEY));

  useEffect(() => {
    if (isStandalone() || !isMobile()) { setHidden(true); return undefined; }
    setIos(/iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream);
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => setHidden(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    const timer = setTimeout(() => setReady(true), 4000);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); clearTimeout(timer); };
  }, []);

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, "1"); setHidden(true); };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setHidden(true);
    setDeferred(null);
  };

  if (hidden || !ready) return null;
  let hint = <p className="text-xs text-parchment/80">Quick access to dues, events and notices from your home screen.</p>;
  if (ios) hint = <p className="text-xs text-parchment/80">Tap <Share size={12} className="inline" /> <strong>Share</strong>, then <strong>Add to Home Screen</strong>.</p>;
  else if (!deferred) hint = <p className="text-xs text-parchment/80">Open the browser menu <MoreVertical size={12} className="inline" /> and tap <strong>Add to Home screen</strong> or <strong>Install app</strong>.</p>;
  return (
    <div className="fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+4.5rem)] z-[60] mx-auto max-w-md rounded-2xl border border-gold/50 bg-crimson p-4 pr-10 text-parchment shadow-2xl animate-in fade-in slide-in-from-bottom-4 sm:inset-x-auto sm:right-4" data-testid="install-banner">
      <button onClick={dismiss} className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full text-parchment/70 hover:bg-white/10" aria-label="Dismiss" data-testid="install-dismiss"><X size={16} /></button>
      <div className="flex items-center gap-3">
        <img src="/icon-192.png" alt="" className="h-12 w-12 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <p className="font-serif-display text-base font-bold">Install the Okuapemman app</p>
          {hint}
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        {deferred && <button onClick={install} className="flex h-10 flex-1 items-center justify-center gap-1 rounded-full bg-gold text-sm font-semibold text-crimson hover:bg-gold/90" data-testid="install-btn"><Download size={15} /> Install</button>}
        <button onClick={dismiss} className="h-10 flex-1 rounded-full border border-parchment/40 text-sm font-semibold text-parchment/90" data-testid="install-later">Not now</button>
      </div>
    </div>
  );
}
